import { QueryClient, VueQueryPlugin } from '@tanstack/vue-query'
import { experimental_createQueryPersister } from '@tanstack/query-persist-client-core'
import { get, set, del, delMany, entries, keys } from 'idb-keyval'
import { getActivePinia } from 'pinia'
import type { App } from 'vue'

const PERSIST_PREFIX = 'tanstack-query'

type QueryPersister = ReturnType<typeof experimental_createQueryPersister>

/**
 * Identität der persistierten Abfragen: Mandant, angemeldeter User und „Anmelden als“ (dieselbe
 * Quelle wie der lia-Header). Gelesen aus dem Pinia-Zustand statt per Store-Import (Importzyklus
 * user-Store → queryClient). Ohne bekannte Identität `null`: dann wird nichts persistiert.
 */
function identityScope(): string | null {
	const s = getActivePinia()?.state.value
	const subdomain = s?.backend?.url?.subdomain
	const userId = s?.lia?.authenticated_user?.id ?? s?.user?.whoami?.id
	if (!subdomain || !userId) return null
	return `${subdomain}:${userId}:${s?.lia?.person?.user_id ?? 0}:`
}

const persisters = new Map<string, QueryPersister>()

function scopedPersister(scope: string): QueryPersister {
	let p = persisters.get(scope)
	if (!p) {
		p = experimental_createQueryPersister({
			storage: {
				getItem: (key: string) => get(key),
				setItem: (key: string, value: string) => set(key, value),
				removeItem: (key: string) => del(key),
				entries: () => entries<string>(),
			},
			maxAge: 1000 * 60 * 60 * 24, // 24 hours
			// Schlüssel `<mandant>:<user>:<lia>:tanstack-query-<hash>`: ein zweiter Tab (Admin + „Anmelden als“)
			// stellt nie die Daten einer anderen Identität wieder her
			prefix: `${scope}${PERSIST_PREFIX}`,
		})
		persisters.set(scope, p)
	}
	return p
}

// Identität beim Start der Abfrage festhalten: eine Antwort, die erst nach einem Identitätswechsel
// eintrifft, landet so unter der Identität, mit der sie angefragt wurde
export const persister: Pick<QueryPersister, 'persisterFn'> = {
	persisterFn: (queryFn, ctx, query) => {
		const scope = identityScope()
		return scope
			? scopedPersister(scope).persisterFn(queryFn, ctx, query)
			: Promise.resolve(queryFn(ctx))
	},
}

/**
 * Aufräumen bei Identitätswechsel (Login, Logout, Anmelden als, Zurück zum eigenen Nutzer). Löscht IMMER
 * alle persistierten Abfragen aller Identitäten (Datenminimierung): so bleiben auch die Daten des
 * übernommenen Nutzers nicht zurück, wenn „Anmelden als“ endet. Das Promise löst erst nach dem Löschen
 * auf, Aufrufer warten darauf, bevor die nächste Sitzung beginnt (kein Timeout-Race).
 */
export async function clearPersistedQueries() {
	if (identityScope()) {
		// Kein clear(): danach hingen aktive Observer an entfernten Queries und luden bis zum Remount nicht
		// mehr nach. Inaktive entfernen, aktive zurücksetzen (Daten weg, Neuladen mit der neuen Identität).
		queryClient.removeQueries({ type: 'inactive' })
		void queryClient.resetQueries({ type: 'active' })
	} else {
		// Abgemeldet: nichts mehr neu laden
		queryClient.clear()
	}
	try {
		const persisted = (await keys()).filter((key) =>
			String(key).includes(`${PERSIST_PREFIX}-`)
		)
		await delMany(persisted)
	} catch {
		// ohne IndexedDB (z.B. privater Modus) wurde auch nichts persistiert
	}
}

// AAR-1483: Diese defaultOptions galten bis dato NIE — der Default-Export war
// das nackte VueQueryPlugin, das ohne Optionen installiert wurde und sich
// intern einen eigenen QueryClient mit Stock-Defaults anlegte (staleTime 0,
// gcTime 5 Min., Fokus-/Mount-/Reconnect-Refetch an, kein Persister). Der
// Wrapper-Plugin-Export unten verdrahtet erstmals DIESEN Client in die App.
//
// Bewusste Policy gegenüber den Stock-Defaults:
// - staleTime 5 Min. als Drossel: Fokus-/Mount-/Reconnect-Refetch greifen nur
//   für tatsächlich stale Daten (vorher: staleTime 0 = Refetch bei jedem Anlass).
// - gcTime 30 Min. statt 5 Min.
// - refetchOnWindowFocus/Mount/Reconnect bleiben an (= Stock-Verhalten, das die
//   App global effektiv schon immer hatte). Ausnahme: Tabellen (useTable) hatten
//   bisher ein refetchOnWindowFocus: false-Override, das in diesem PR entfernt
//   wird — dort ist der Fokus-Refetch also neu (Mount/Reconnect waren auch in
//   useTable schon an).
// - KEIN globaler Persister: der experimentelle IndexedDB-Persister war durch
//   die fehlende Verdrahtung nie aktiv und wird hier bewusst NICHT als
//   Nebenwirkung aktiviert (separates Follow-up). Der Export bleibt für die
//   per-Query-Nutzung in useTable (usePersister/offline) bestehen.
export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: (import.meta.env.VITE_QUERY_STALE_TIME || 5) * 60 * 1000,
			gcTime: (import.meta.env.VITE_QUERY_GC_TIME || 30) * 60 * 1000,
			refetchOnWindowFocus: true,
			refetchOnMount: true,
			refetchOnReconnect: true,
		},
	},
})

// Wrapper statt nacktem VueQueryPlugin: installiert das Plugin MIT dem oben
// konfigurierten Client. `app.use(VueQueryPlugin)` ohne Optionen würde einen
// zweiten, unkonfigurierten Client erzeugen (der Bug, der die gesamte
// Konfiguration hier wirkungslos machte).
const queryClientPlugin = {
	install(app: App) {
		app.use(VueQueryPlugin, { queryClient })
	},
}

export default queryClientPlugin
